import {useCallback, useMemo, ReactNode} from 'react';

import {useAnalytics} from '@stripe-internal/inline-data';
import {Form as RACForm} from '@stripe-internal/conversations-ui';
import {getCountries, CountryCode} from 'libphonenumber-js';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import {FormContainer} from './FormContainer';

export const messages = defineMessages({
  genericErrorMessage: {
    id: 'support_conversations_client.rac.form.genericErrorMessage',
    description: 'Error message indicating a generic stripe error',
    defaultMessage: 'Something went wrong. Please try again.',
  },
  fieldErrorMessage: {
    id: 'support_conversations_client.rac.form.fieldErrorMessage',
    description:
      'Error message indicating there is an issue with one of the form field values',
    defaultMessage: 'All fields are required.',
  },
  rateLimitedErrorMessage: {
    id: 'support_conversations_client.rac.form.rateLimitedErrorMessage',
    description:
      'Error message indicating the user is rate limited from Request A Call',
    defaultMessage:
      'You have submitted too many requests. Please wait and try again later.',
  },
  countryCodeErrorMessage: {
    id: 'support_conversations_client.rac.form.countryCodeErrorMessage',
    description:
      "Error message indicating the user's country code is unsupported from Request A Call",
    defaultMessage:
      'The country code is unsupported. Please use a different country code or contact method.',
  },
});

export type RACClientErrors =
  | 'rac-generic'
  | 'rac-country-code'
  | 'rac-ineligible'
  | 'rac-invalid-argument'
  | 'rac-rate-limit-exceeded'
  | 'rac-uh-conversation-not-found'
  | 'rac-unauthorized'
  | 'rac-country-code';

export interface Props {
  initialName: string;
  initialPhoneNumber: string;
  initialMessage?: string;
  clientError?: RACClientErrors;
  handleSubmit: (
    name: string,
    phoneNumber: string,
    message: string,
  ) => Promise<void>;
  handleCancel: () => void;
  submitting: boolean;
}

export const RequestACallForm = ({
  initialName,
  initialPhoneNumber,
  initialMessage,
  clientError,
  handleSubmit,
  handleCancel,
  submitting,
}: Props) => {
  const analytics = useAnalytics();
  const {locale} = useIntl();

  const countryCode = useMemo(() => {
    if (locale === 'es-419') {
      return 'MX';
    } else if (['zh-Hans', 'zh-Hant'].includes(locale)) {
      return 'CN';
    } else {
      const localeComponents = locale.split('-');
      const country = localeComponents[localeComponents.length - 1];
      const countries = getCountries();
      if (country && country in countries) {
        return country as CountryCode;
      }
    }

    return undefined;
  }, [locale]);

  const ClientError = useMemo((): ReactNode => {
    switch (clientError) {
      case 'rac-generic':
      case 'rac-unauthorized':
      case 'rac-ineligible':
        return <FormattedMessage {...messages.genericErrorMessage} />;
      case 'rac-invalid-argument':
        return <FormattedMessage {...messages.fieldErrorMessage} />;
      case 'rac-rate-limit-exceeded':
        return <FormattedMessage {...messages.rateLimitedErrorMessage} />;
      case 'rac-country-code':
        return <FormattedMessage {...messages.countryCodeErrorMessage} />;
      default:
        return null;
    }
  }, [clientError]);

  const onCancel = useCallback(() => {
    analytics.action('support_conversations_rac_cancel');
    handleCancel();
  }, [analytics, handleCancel]);

  return (
    <FormContainer>
      <RACForm
        initialName={initialName}
        initialPhoneNumber={initialPhoneNumber}
        initialMessage={initialMessage}
        defaultCountry={countryCode}
        handleSubmit={handleSubmit}
        handleCancel={onCancel}
        hideCancelButton
        clientError={ClientError}
        loading={submitting}
      />
    </FormContainer>
  );
};
